import type { CopyKey } from 'locales/copy-keys.gen'
import { formatUtcDate } from 'utils/formatUtcDate'
import { changeBetween, pointsInRange, returnStats, type ChartPoint } from 'models/chart'
import type { Project } from 'models/project'
import {
  COMPUTED_FIELDS,
  type ChartDenomination,
  type ChartKind,
  type ComputedValues,
  type ProductChart,
  type ProductChartView,
} from './types'

/** What every chart kind shares: its date line and its CSV history. */

/** Date of the latest point, as the "As of" footnote shows it. */
export const latestDate = (points: ChartPoint[]): string | undefined => {
  const latest = points[points.length - 1]
  return latest ? formatUtcDate(latest[0]) : undefined
}

/** The points a denomination's returns are measured on (see `returnIndex`). */
export const returnPoints = (denomination: ChartDenomination): ChartPoint[] =>
  denomination.returnIndex ?? denomination.points

const HISTORY_HEADER: Record<ChartKind, CopyKey> = {
  stock: 'stoPage.performance.closeHeader',
  fund: 'stoPage.performance.unitNavHeader',
  miningNote: 'stoPage.performance.valueHeader',
}

/** `operationsStart`: a fund's start of operations (UTC ms, back-office General tab), what it annualizes from. */
export const toProductChartView = (chart: ProductChart, operationsStart?: number): ProductChartView => ({
  chart,
  statsByCurrency:
    chart.kind === 'fund'
      ? Object.fromEntries(
          chart.denominations.map((denomination) => [
            denomination.currency,
            returnStats(returnPoints(denomination), operationsStart),
          ]),
        )
      : {},
  history: chart.denominations.map(({ currency, points }) => ({
    currency,
    headerKey: HISTORY_HEADER[chart.kind],
    points,
  })),
})

const PERFORMANCE_KINDS: ReadonlySet<ChartKind> = new Set(['fund', 'stock'])

export const isComputedFieldPath = (path: string): boolean => path.startsWith('computed.')

/** A note's inception: its first sale's start, else the project's opening time. */
export const inceptionTime = (project: Pick<Project, 'sales' | 'openingTime'> | undefined): number | undefined => {
  const starts = (project?.sales ?? []).map((sale) => new Date(sale.startDate ?? '').getTime()).filter(Number.isFinite)
  const opening = new Date(project?.openingTime ?? '').getTime()
  const times = starts.length ? starts : Number.isFinite(opening) ? [opening] : []
  return times.length ? Math.min(...times) : undefined
}

const changeOver = (points: ChartPoint[]): number | undefined =>
  points.length < 2 ? undefined : changeBetween(points[0][1], points[points.length - 1][1]).percent

/**
 * YTD: the latest point against the last one before 1 January (as the chart's YTD range).
 * Since inception: a fund from its first NAV; an equity note from the underlying's close on the
 * note's inception day (the stock's history starts long before the note), none without that date.
 */
export const toComputedValues = (
  kind: ChartKind,
  points: ChartPoint[],
  project?: Pick<Project, 'sales' | 'openingTime'>,
): ComputedValues => {
  if (!PERFORMANCE_KINDS.has(kind) || points.length < 2) return {}

  let sinceInception: number | undefined
  if (kind === 'fund') {
    sinceInception = changeOver(points)
  } else {
    const inception = inceptionTime(project)
    // The close on or before inception day is the baseline; a note older than the data starts at its first close.
    if (inception !== undefined) {
      const firstAfter = points.findIndex(([time]) => time > inception)
      const baseline = firstAfter === -1 ? points.length - 1 : Math.max(0, firstAfter - 1)
      sinceInception = changeOver(points.slice(baseline))
    }
  }

  const values: ComputedValues = {}
  const ytd = changeOver(pointsInRange(points, 'YTD'))
  if (ytd !== undefined) values[COMPUTED_FIELDS.performanceYtd] = { value: ytd }
  if (sinceInception !== undefined) values[COMPUTED_FIELDS.performanceSinceInception] = { value: sinceInception }
  return values
}

/**
 * A fund's total NAV now: the latest point of its total NAV (unit NAV × units outstanding), in
 * `currency` — never another one (the page would mix currencies). None without its units.
 */
export const latestFundNav = (chart: Pick<ProductChart, 'fundNavs'>, currency?: string): ComputedValues => {
  const navs = chart.fundNavs?.find((nav) => nav.currency === currency)
  const latest = navs?.points[navs.points.length - 1]
  return navs && latest ? { [COMPUTED_FIELDS.fundNav]: { value: latest[1], currency: navs.currency } } : {}
}

/** An equity note's underlying stock now: its latest close, in the stock's own currency, to the cent. */
export const latestUnderlyingPrice = (chart: Pick<ProductChart, 'kind' | 'denominations'>): ComputedValues => {
  if (chart.kind !== 'stock') return {}
  const [{ currency, points }] = chart.denominations
  const latest = points[points.length - 1]
  return latest ? { [COMPUTED_FIELDS.underlyingPrice]: { value: latest[1], currency, decimals: 2 } } : {}
}

/** A mining note's BTC mined per note so far, to the satoshi. */
export const latestBtcMined = (chart: Pick<ProductChart, 'note'>): ComputedValues => {
  const minedBtc = chart.note?.minedBtc
  return minedBtc === undefined ? {} : { [COMPUTED_FIELDS.btcMined]: { value: minedBtc, currency: 'BTC', decimals: 8 } }
}
